import { useEffect, useState } from 'react'

const SEEN_KEY = 'djay_seen_achievements'
const SHIMMER_DURATION_MS = 2000

/**
 * useAchievementShimmer — one-shot "newly seen" shimmer.
 *
 * Tracks which achievements the learner has already laid eyes on (persisted in
 * localStorage). The first time an unlocked achievement is rendered it returns
 * `true` for a short window so the card can play a celebratory shine, then
 * settles back to `false` and never shines again on subsequent visits.
 */
export function useAchievementShimmer(
  achievementId: string,
  isUnlocked: boolean,
): boolean {
  const [isNewlySeen, setIsNewlySeen] = useState(false)

  useEffect(() => {
    if (!isUnlocked) return
    try {
      const stored = localStorage.getItem(SEEN_KEY)
      const seenSet = new Set<string>(stored ? JSON.parse(stored) : [])
      if (seenSet.has(achievementId)) return

      seenSet.add(achievementId)
      localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seenSet)))
      setIsNewlySeen(true)

      const timer = setTimeout(() => setIsNewlySeen(false), SHIMMER_DURATION_MS)
      return () => clearTimeout(timer)
    } catch (e) {
      console.error('Failed to update seen achievements', e)
    }
  }, [achievementId, isUnlocked])

  return isNewlySeen
}
